/* =========================================================
   COLLECTION TOOLBAR — Filter icon injection + Sort dropdown
   - Injects an <img> with the filter icon into the Filter button
   - Replaces native <select> with a custom vertical panel
     (Moncler-style). Theme's facet-short logic still receives
     the change event on the hidden native <select>.
   - Uses MutationObserver to re-init after AJAX facet reloads
     (theme replaces .facet-short element wholesale on sort change).
   ========================================================= */

(function () {
  'use strict';

  const FILTER_ICON_URL =
    'https://cdn.shopify.com/s/files/1/0727/2149/8453/files/filter.svg?v=1776796754';

  function injectFilterIcon() {
    const filterBtn = document.querySelector(
      '.collection__toolbar .facet-drawer-button'
    );
    if (!filterBtn) return;
    if (filterBtn.querySelector('.msk-filter-icon')) return;

    const img = document.createElement('img');
    img.src = FILTER_ICON_URL;
    img.className = 'msk-filter-icon';
    img.alt = '';
    img.setAttribute('aria-hidden', 'true');
    img.width = 16;
    img.height = 16;
    filterBtn.appendChild(img);
  }

  function initSortDropdown() {
    const facetShort = document.querySelector(
      '.collection__toolbar > .gap-10 .facet-short'
    );
    if (!facetShort) return;

    const select = facetShort.querySelector('select[name="sort_by"]');
    if (!select) return;

    // Guard against double-init on same element
    if (facetShort.dataset.mskSortInitialized === 'true') return;
    facetShort.dataset.mskSortInitialized = 'true';

    // Build the panel fresh
    const panel = document.createElement('div');
    panel.className = 'msk-sort-panel';
    panel.setAttribute('role', 'listbox');

    Array.from(select.options).forEach(function (opt) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className =
        'msk-sort-panel__item' + (opt.selected ? ' is-active' : '');
      btn.dataset.value = opt.value;
      btn.textContent = opt.text;
      btn.setAttribute('role', 'option');
      btn.setAttribute('aria-selected', opt.selected ? 'true' : 'false');

      btn.addEventListener('click', function (e) {
        e.stopPropagation();

        panel
          .querySelectorAll('.msk-sort-panel__item')
          .forEach(function (b) {
            b.classList.remove('is-active');
            b.setAttribute('aria-selected', 'false');
          });
        btn.classList.add('is-active');
        btn.setAttribute('aria-selected', 'true');

        // Drive the native select — theme's facet-short custom element
        // listens for change and triggers AJAX refetch
        select.value = opt.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));

        closePanel();
      });

      panel.appendChild(btn);
    });

    facetShort.appendChild(panel);

    function openPanel() {
      panel.classList.add('is-open');
      facetShort.classList.add('is-open');
    }
    function closePanel() {
      panel.classList.remove('is-open');
      facetShort.classList.remove('is-open');
    }
    function togglePanel() {
      if (panel.classList.contains('is-open')) closePanel();
      else openPanel();
    }

    facetShort.addEventListener('click', function (e) {
      if (e.target.closest('.msk-sort-panel')) return;
      togglePanel();
    });

    // Outside-click close: attach per-instance with named handler
    // so it's cleaned up when this facet-short is replaced
    const onDocClick = function (e) {
      if (!document.contains(facetShort)) {
        document.removeEventListener('click', onDocClick);
        document.removeEventListener('keydown', onDocKeydown);
        return;
      }
      if (!facetShort.contains(e.target)) closePanel();
    };
    const onDocKeydown = function (e) {
      if (!document.contains(facetShort)) {
        document.removeEventListener('click', onDocClick);
        document.removeEventListener('keydown', onDocKeydown);
        return;
      }
      if (e.key === 'Escape' && panel.classList.contains('is-open')) {
        closePanel();
      }
    };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onDocKeydown);

    // Sync active state if select is changed externally
    select.addEventListener('change', function () {
      panel
        .querySelectorAll('.msk-sort-panel__item')
        .forEach(function (b) {
          const active = b.dataset.value === select.value;
          b.classList.toggle('is-active', active);
          b.setAttribute('aria-selected', active ? 'true' : 'false');
        });
    });
  }

  function init() {
    injectFilterIcon();
    initSortDropdown();
  }

  // ---- Bootstrap ----
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Standard Shopify section reload (theme editor, some section re-renders)
  document.addEventListener('shopify:section:load', init);

  // ---- CRITICAL: watch for AJAX facet replacements ----
  // Theme's facet-short custom element replaces its own DOM on sort change
  // via AJAX (no shopify:section:load event is fired), so we need a
  // MutationObserver on the toolbar container to re-init.
  function installToolbarObserver() {
    const toolbar = document.querySelector('.collection__toolbar');
    if (!toolbar) return;
    if (toolbar.dataset.mskObserverInstalled === 'true') return;
    toolbar.dataset.mskObserverInstalled = 'true';

    const observer = new MutationObserver(function (mutations) {
      let needsReinit = false;
      for (const m of mutations) {
        if (m.type !== 'childList') continue;
        // If any added node contains .facet-short or the filter button,
        // re-init. Also if .facet-short lost its initialized flag.
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (
            node.matches?.('.facet-short, .facet-drawer-button') ||
            node.querySelector?.('.facet-short, .facet-drawer-button')
          ) {
            needsReinit = true;
            break;
          }
        }
        if (needsReinit) break;
      }

      if (needsReinit) {
        // Small delay to let theme finish its own setup
        setTimeout(init, 0);
      }
    });

    observer.observe(toolbar, { childList: true, subtree: true });
  }

  // Install observer after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', installToolbarObserver);
  } else {
    installToolbarObserver();
  }

  // Re-install observer after full section reload (observer is tied to
  // a specific .collection__toolbar element which may be replaced)
  document.addEventListener('shopify:section:load', function () {
    installToolbarObserver();
  });
})();